import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Send, User, Briefcase, Phone as PhoneIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSendSMS, useSMSSettings } from '@/hooks/useSMS';
import { useCustomers } from '@/hooks/useCustomers';
import { useLeads } from '@/hooks/useLeads';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface NewMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SelectedRecipient {
  phone: string;
  name: string;
  customerId?: string;
  leadId?: string;
}

const PHONE_LIKE = /^[+\d][\d\s\-()]{5,}$/;

export function NewMessageDialog({ open, onOpenChange }: NewMessageDialogProps) {
  const queryClient = useQueryClient();
  const sendSMS = useSendSMS();
  const { data: settings } = useSMSSettings();

  const [recipientSearch, setRecipientSearch] = useState('');
  const [selected, setSelected] = useState<SelectedRecipient | null>(null);
  const [message, setMessage] = useState('');

  const debouncedSearch = useDebouncedValue(recipientSearch, 250);
  const shouldSearch = !selected && debouncedSearch.trim().length >= 2;

  const { data: customers, isFetching: customersLoading } = useCustomers(
    shouldSearch ? debouncedSearch : undefined
  );
  const { data: leads, isFetching: leadsLoading } = useLeads(
    undefined,
    shouldSearch ? debouncedSearch : undefined
  );

  const customerResults = useMemo(() => {
    if (!shouldSearch) return [];
    return (customers || [])
      .filter((c) => !!c.primary_phone)
      .slice(0, 5);
  }, [customers, shouldSearch]);

  const leadResults = useMemo(() => {
    if (!shouldSearch) return [];
    return (leads || [])
      .filter((l) => !!l.customer_phone)
      .slice(0, 5);
  }, [leads, shouldSearch]);

  const rawPhoneOption = useMemo(() => {
    if (selected) return null;
    const trimmed = recipientSearch.trim();
    if (!PHONE_LIKE.test(trimmed)) return null;
    // Suppress raw option if a result already matches this exact phone
    const phoneDigits = trimmed.replace(/\D/g, '');
    const exactMatch =
      customerResults.some((c) => (c.primary_phone || '').replace(/\D/g, '') === phoneDigits) ||
      leadResults.some((l) => (l.customer_phone || '').replace(/\D/g, '') === phoneDigits);
    if (exactMatch) return null;
    return trimmed;
  }, [recipientSearch, selected, customerResults, leadResults]);

  const showResults =
    shouldSearch &&
    (customerResults.length > 0 ||
      leadResults.length > 0 ||
      rawPhoneOption !== null ||
      customersLoading ||
      leadsLoading);

  const reset = () => {
    setRecipientSearch('');
    setSelected(null);
    setMessage('');
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePickCustomer = (c: { id: string; customer_name: string; primary_phone: string | null }) => {
    if (!c.primary_phone) return;
    setSelected({ phone: c.primary_phone, name: c.customer_name, customerId: c.id });
    setRecipientSearch('');
  };

  const handlePickLead = (l: { id: string; customer_name: string; customer_phone: string | null }) => {
    if (!l.customer_phone) return;
    setSelected({ phone: l.customer_phone, name: l.customer_name, leadId: l.id });
    setRecipientSearch('');
  };

  const handlePickRawPhone = (phone: string) => {
    setSelected({ phone, name: phone });
    setRecipientSearch('');
  };

  const handleSend = async () => {
    if (!selected || !message.trim()) return;
    try {
      await sendSMS.mutateAsync({
        to: selected.phone,
        content: message.trim(),
        lead_id: selected.leadId,
        customer_id: selected.customerId,
      });
      queryClient.invalidateQueries({ queryKey: ['omni-channel-threads'] });
      reset();
      onOpenChange(false);
    } catch {
      // toast handled in useSendSMS
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>
            Send an SMS to a customer, lead, or any phone number.
          </DialogDescription>
        </DialogHeader>

        {settings && !settings.enabled && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            SMS is not enabled. Configure CallTrackingMetrics in Settings &gt; Integrations.
          </div>
        )}

        <div className="space-y-4 py-2">
          {/* Recipient */}
          <div className="space-y-2">
            <Label htmlFor="recipient">Recipient</Label>
            {selected ? (
              <div className="flex items-center justify-between rounded-md border border-input bg-muted/30 px-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{selected.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {selected.phone}
                    {selected.customerId && ' • Customer'}
                    {selected.leadId && ' • Lead'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => setSelected(null)}
                  aria-label="Change recipient"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="recipient"
                  autoFocus
                  placeholder="Search by name or phone, or type a number..."
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                />
                {showResults && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
                    <ScrollArea className="max-h-64">
                      <div className="p-1">
                        {customersLoading || leadsLoading ? (
                          <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                            Searching...
                          </div>
                        ) : (
                          <>
                            {customerResults.length > 0 && (
                              <div>
                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                                  Customers
                                </p>
                                {customerResults.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => handlePickCustomer(c)}
                                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                                  >
                                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="flex-1 truncate">{c.customer_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {c.primary_phone}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {leadResults.length > 0 && (
                              <div>
                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                                  Leads
                                </p>
                                {leadResults.map((l) => (
                                  <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => handlePickLead(l)}
                                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                                  >
                                    <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="flex-1 truncate">{l.customer_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {l.customer_phone}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {rawPhoneOption && (
                              <div>
                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                                  New number
                                </p>
                                <button
                                  type="button"
                                  onClick={() => handlePickRawPhone(rawPhoneOption)}
                                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                                >
                                  <PhoneIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span className="flex-1 truncate">
                                    Send to {rawPhoneOption}
                                  </span>
                                </button>
                              </div>
                            )}
                            {customerResults.length === 0 &&
                              leadResults.length === 0 &&
                              !rawPhoneOption && (
                                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                                  No matches. Type a phone number to send to a new contact.
                                </p>
                              )}
                          </>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Write your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={!selected}
            />
            <p className="text-xs text-muted-foreground">
              {message.length}/160 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              !selected ||
              !message.trim() ||
              sendSMS.isPending ||
              !settings?.enabled
            }
          >
            {sendSMS.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
